import { ApiErrorHandlerService } from './services/api-error-handler/api-error-handler';
import { ApiService } from './services/mibp-api-services/_mibp-api-generated.service';
import { ArrayHelperService } from './services/array-helper/array-helper.service';
import { AuthService } from './services/auth-service/auth.service';
import { BroadcastService } from './services/broadcast-service/broadcast.service';
import { ClientIdService } from './services/clientid-service/clientid.service';
import { ClientSideCacheService } from './services/client-side-cache/client-side-cache.service';
import { ContactUsService } from './services/contact-us/contact-us.service';
import { FormattingService } from './services/formatting/formatting.service';
import { FormValidationService } from './services/form-validation/form-validation.service';
import { GlobalConfigService } from './services/global-config/global-config.service';
import { InlineEditingOverlayService } from './services/inline-editing-overlay/inline-editing-overlay.service';
import { LoaderService } from './services/loader/loader.service';
import { LocalizationService } from './services/localization/localization.service';
import { LogService } from './services/logservice/log.service';
import { MibpDOMHelperService } from './services/dom-helper/dom-helper.service';
import { MibpErrorHandler } from './services/error-handler/error-handler.service';
import { MibpGridService } from './shared/components/data/mibp-grid/services/mibp-grid.service';
import { MibpHtmlHelperService } from './services/html-helper/html-helper.service';
import { MibpHttpApi } from './services/mibp-http-api/mibp-http-api.service';
import { MibpResponsiveService } from './services/responsive/responsive.service';
import { PartsCatalogueService } from './services/parts-catalogue/parts-catalogue.service';
import { PermissionService } from './services/permission/permission.service';
import { ResourceStringEditorService } from './services/resource-string-editor/resource-string-editor.service';
import { ResponsibilityPickerService } from './services/responsibility-picker/responsibility-picker.service';
import { ScriptService } from './services/script-service/script.service';
import { ScrollToService } from './services/scroll-to/scroll-to.service';
import { SignalRService } from './services/signalr-service/signalr.service';
import { StartupAuthenticationService } from './services/startup/startup-authentication.service';
import { StartupBackendService } from './services/startup/startup-backend.service';
import { StartupLanguageService } from './services/startup/startup-language.service';
import { StartupLocalizationService } from './services/startup/startup-localization.service';
import { StartupService } from './services/startup/startup.service';
import { StartupUserPermissionsService } from './services/startup/startup-user-permissions.service';
import { StartupUserStatusService } from './services/startup/startup-user-status.service';
import { ToastService } from 'root/shared/services/toast-service/toast.service';
import { UrlHelperService } from './services/url-helper/url-helper.service';
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { ApplicationStateService } from './services/application-state/application-state.service';
import { appRoutes } from './routes';
import { AuthInterceptor } from './services/auth-service/auth.interceptor';
import { ClientSideCacheMemoryStorageService } from './services/client-side-cache/memoryStorage';
import { DocumotoAuthService } from './services/auth-service/documoto-auth.service';
import { DownloadedManualsService } from './services/downloaded-manuals/downloaded-manuals.service';
import { ElectonicManualDetailPageService } from './services/electronic-manual-detail-page/electronic-manual-detail-page.service';
import { EncryptionService } from './services/encryption/encryption-service';
import { environment } from 'root/environment';
import { FrontendContextService } from './services/front-end-context/front-end-context.service';
import { GoogleTagManagerService } from './services/google-tag-manager-service/google-tag-manager.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { MibpDateTimeService } from './services/datetime/datetime.service';
import { MibpFeatureTourService } from './components/feature-tour/feature-tour.service';
import { MibpInternalUserPhotoService } from './components/navigation';
import { MibpJobsService } from './services/mibp-jobs/mibp-jobs.service';
import { MibpOnboardingService } from './components/onboarding/onboarding.service';
import { MibpOperationsService } from './services/operations/operations.service';
import { MibpSessionRevokedService } from './services/session-revoked/session-revoked.service';
import { MibpSessionService } from './services/mibp-session/mibp-session.service';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { loadGlobalConfig, MSALGuardConfigFactory, MSALInstanceFactory } from './app.factories';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { MSAL_INSTANCE, MSAL_GUARD_CONFIG, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { provideNgIdle } from '@ng-idle/core';
import { MyfleetEquipmentStatusDescriptionService } from './components/equipment-status-desc/equipment-status-desc.service';
import { PopupContainerInternalService } from './shared/components/popovers/popup-container/popup-container-internal.service';
import { StartupGuardService } from './guards';
import { TileService } from './modules/home/modules/home-dashboard/components/dashboard-tile/tile.service';
import { OperationSiteService } from './services/operation-sites/operation-sites.service';
import { SidebarService } from './services/sidebar-service/sidebar.service';
import { provideMibpDateAdapter } from './shared/components/form/datepicker/datepicker.dateadapter';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes),
    provideAnimations(),
    ...provideNgIdle(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideNativeDateAdapter(), // Defaults
    provideMibpDateAdapter(), // Our override

    /// TODO: Review withDefaultRegisterables - it adds all Chart.js components to the bundle, which might not be necessary.
    // Consider creating a custom configuration with only the needed components to reduce bundle size.  
    // Also look into defer load in isolated components
    provideCharts(withDefaultRegisterables()),
    DownloadedManualsService,
    LocalizationService,
    ScrollToService,
    AuthService,
    DocumotoAuthService,
    GoogleTagManagerService,
    StartupGuardService,
    ClientSideCacheService,
    SignalRService,
    ApiService,
    FrontendContextService,
    ResourceStringEditorService,
    FormattingService,
    { provide: ErrorHandler, useClass: MibpErrorHandler },
    ClientSideCacheMemoryStorageService,
    FormValidationService,
    StartupService,
    StartupLanguageService,
    StartupAuthenticationService,
    StartupBackendService,
    StartupLocalizationService,
    // StartupUserEventService,
    StartupUserStatusService,
    StartupUserPermissionsService,
    ContactUsService,
    SidebarService,
    { provide: Window, useValue: window },
    { provide: "windowObject", useValue: window },
    ResponsibilityPickerService,
    LogService,
    PartsCatalogueService,
    LoaderService,
    MibpHttpApi,
    UrlHelperService,
    MibpGridService,
    ArrayHelperService,
    PermissionService,
    BroadcastService,
    InlineEditingOverlayService,
    ApplicationStateService,
    PopupContainerInternalService,
    MibpInternalUserPhotoService,
    MibpResponsiveService,
    MibpOperationsService,
    ToastService,
    MibpJobsService,
    provideAppInitializer(() => loadGlobalConfig(inject(GlobalConfigService))()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor, multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ClientIdService,
    MibpOnboardingService,
    MibpFeatureTourService,
    MyfleetEquipmentStatusDescriptionService,
    MibpDOMHelperService,
    OperationSiteService,
    ApiErrorHandlerService,
    MibpSessionService,
    MibpHtmlHelperService,
    MibpDateTimeService,
    EncryptionService,
    ScriptService,
    MibpSessionRevokedService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ElectonicManualDetailPageService,

    TileService

  ]
};
